import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Spinner } from 'react-bootstrap'
import { Navigate, useNavigate } from 'react-router-dom'
import { LuGraduationCap } from 'react-icons/lu'
import {
  BsArrowClockwise,
  BsArrowLeft,
  BsCalendar3,
  BsCheckCircleFill,
  BsCreditCard2Front,
  BsPeople,
} from 'react-icons/bs'
import 'bootstrap/dist/css/bootstrap.min.css'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import { useSubscriptionNeedPlan } from './commonComponents/SubscriptionNeedPlanContext'

const formatPaiseAsInr = (amountInPaise, currency = 'INR') => {
  if (amountInPaise == null || amountInPaise === '') return '-'
  const amountInRupees = Number(amountInPaise) / 100
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0,
  }).format(amountInRupees)
}

const formatBillingCycle = (cycle) => {
  if (!cycle) return ''
  return String(cycle).replace(/_/g, ' ')
}

const BILLING_CYCLE_ORDER = ['monthly', 'quarterly', 'yearly']
const BILLING_CYCLE_TAB_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  others: 'Others',
}

const normalizeBillingCycle = (cycle) => {
  const key = String(cycle || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ')

  if (['monthly', 'month'].includes(key)) return 'monthly'
  if (['quarterly', 'quarter'].includes(key)) return 'quarterly'
  if (['yearly', 'year', 'annual', 'annually'].includes(key)) return 'yearly'
  if (!key) return 'others'
  return 'others'
}

const groupPlansByBillingCycle = (plans) => {
  const groups = new Map()

  plans.forEach((plan) => {
    const key = normalizeBillingCycle(plan.billing_cycle)
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key).push(plan)
  })

  const orderedKeys = [
    ...BILLING_CYCLE_ORDER.filter((key) => groups.has(key)),
    ...(groups.has('others') ? ['others'] : []),
  ]

  return orderedKeys.map((key) => ({
    key,
    label: BILLING_CYCLE_TAB_LABELS[key],
    plans: groups.get(key).sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.amount - b.amount,
    ),
  }))
}

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true)
      return
    }
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })

const openRazorpayCheckout = async (checkout, onSuccess) => {
  const loaded = await loadRazorpayScript()
  if (!loaded || !window.Razorpay) {
    toast.error('Unable to load payment gateway. Please try again.')
    return false
  }

  const options = {
    key: checkout.key,
    amount: checkout.amount,
    currency: checkout.currency || 'INR',
    name: checkout.name || 'Institute Subscription',
    description: checkout.description || 'Subscription payment',
    order_id: checkout.order_id,
    prefill: checkout.prefill,
    notes: checkout.notes,
    handler: (response) => {
      onSuccess?.(response)
    },
    theme: { color: '#223843' },
  }

  const razorpay = new window.Razorpay(options)
  razorpay.on('payment.failed', (response) => {
    toast.error(response.error?.description || 'Payment failed. Please try again.')
  })
  razorpay.open()
  return true
}

const isPlanAvailable = (plan) => plan?.is_active !== false

const PlanCard = ({ plan, payingPlanId, onPay, showBillingCycle = true }) => {
  const features = [
    `Up to ${plan.student_limit} students`,
    `${plan.duration_months} month${plan.duration_months > 1 ? 's' : ''} validity`,
  ]

  return (
    <div className="col-md-6 col-xl-4">
      <Card
        className="border-0 shadow-sm h-100"
        style={{
          borderRadius: '14px',
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
      >
        <Card.Body style={{ padding: '28px', display: 'flex', flexDirection: 'column' }}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h4
                className="mb-1"
                style={{
                  fontWeight: 700,
                  color: '#111827',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {plan.name}
              </h4>
              <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>
                {plan.description}
              </p>
            </div>
            {isPlanAvailable(plan) && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#065F46',
                  background: '#ECFDF5',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  whiteSpace: 'nowrap',
                }}
              >
                Available
              </span>
            )}
          </div>

          <div className="mb-4">
            <span
              style={{
                fontSize: '32px',
                fontWeight: 800,
                color: '#223843',
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {formatPaiseAsInr(plan.amount, plan.currency)}
            </span>
            {showBillingCycle && plan.billing_cycle && (
              <span className="text-muted" style={{ fontSize: '14px', marginLeft: '6px' }}>
                / {formatBillingCycle(plan.billing_cycle)}
              </span>
            )}
          </div>

          <ul className="list-unstyled mb-4" style={{ flex: 1 }}>
            {features.map((feature) => (
              <li
                key={feature}
                className="d-flex align-items-center gap-2 mb-2"
                style={{ fontSize: '14px', color: '#374151' }}
              >
                <BsCheckCircleFill size={14} style={{ color: '#10B981', flexShrink: 0 }} />
                {feature}
              </li>
            ))}
          </ul>

          <div className="d-flex gap-2 text-muted mb-4" style={{ fontSize: '13px' }}>
            <span className="d-flex align-items-center gap-1">
              <BsPeople size={14} />
              {plan.student_limit} students
            </span>
            <span className="d-flex align-items-center gap-1">
              <BsCalendar3 size={14} />
              {plan.duration_months} mo
            </span>
          </div>

          <button
            type="button"
            disabled={!isPlanAvailable(plan) || payingPlanId === plan.id}
            onClick={() => onPay(plan)}
            style={{
              width: '100%',
              background: isPlanAvailable(plan) ? '#223843' : '#9CA3AF',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 16px',
              fontWeight: 700,
              fontSize: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: isPlanAvailable(plan) && payingPlanId !== plan.id ? 'pointer' : 'not-allowed',
              opacity: payingPlanId === plan.id ? 0.75 : 1,
            }}
          >
            {payingPlanId === plan.id ? (
              <>
                <Spinner animation="border" size="sm" />
                Processing...
              </>
            ) : (
              <>
                <BsCreditCard2Front size={18} />
                Pay & Subscribe
              </>
            )}
          </button>
        </Card.Body>
      </Card>
    </div>
  )
}

const BillingCycleTabs = ({ groups, activeTab, onTabChange, payingPlanId, onPay }) => {
  const activeGroup = groups.find((group) => group.key === activeTab) || groups[0]

  if (!activeGroup) return null

  return (
    <>
      <div
        className="d-flex flex-wrap gap-2 mb-4 p-1"
        style={{
          background: '#F3F4F6',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          width: 'fit-content',
          maxWidth: '100%',
        }}
      >
        {groups.map((group) => {
          const isActive = group.key === activeGroup.key
          return (
            <button
              key={group.key}
              type="button"
              onClick={() => onTabChange(group.key)}
              style={{
                border: 'none',
                borderRadius: '10px',
                padding: '10px 16px',
                background: isActive ? '#223843' : 'transparent',
                color: isActive ? '#ffffff' : '#374151',
                fontWeight: 700,
                fontSize: '14px',
                fontFamily: 'Inter, system-ui, sans-serif',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              {group.label}
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '999px',
                  background: isActive ? 'rgba(255,255,255,0.18)' : '#E5E7EB',
                  color: isActive ? '#ffffff' : '#6B7280',
                }}
              >
                {group.plans.length}
              </span>
            </button>
          )
        })}
      </div>

      <div className="row g-4">
        {activeGroup.plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            payingPlanId={payingPlanId}
            onPay={onPay}
            showBillingCycle={activeGroup.key === 'others'}
          />
        ))}
      </div>
    </>
  )
}

const AdminSubscriptionPlans = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 992 : false,
  )
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [plans, setPlans] = useState([])
  const [error, setError] = useState('')
  const [payingPlanId, setPayingPlanId] = useState(null)
  const [activeBillingTab, setActiveBillingTab] = useState('')

  const { needPlan, refreshNeedPlanCheck } = useSubscriptionNeedPlan()
  const isAdminUser =
    user?.user_type === 'admin' || (user?.roles || []).includes('admin')
  const canAccessSubscription = isAdminUser || needPlan
  const sidebarWidth = isCollapsed ? 85 : 245

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992)
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchPlans = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await ApiEndpoints.getSubscriptionPlans()
      const list = response.data?.data || []
      setPlans(
        [...list].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.amount - b.amount,
        ),
      )
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'Failed to load subscription plans. Please try again.'
      setError(message)
      setPlans([])
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (canAccessSubscription) {
      fetchPlans()
    }
  }, [canAccessSubscription, fetchPlans])

  const handlePaymentSuccess = async (paymentResponse) => {
    try {
      const response = await ApiEndpoints.verifySubscriptionPayment({
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
      })

      const activatedSubscription = response.data?.data
      const successMessage =
        response.data?.message || 'Payment verified and subscription activated successfully.'

      toast.success(successMessage)
      await refreshNeedPlanCheck()
      navigate('/admin/subscription', {
        replace: true,
        state: { subscription: activatedSubscription },
      })
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          'Payment received but verification failed. Please contact support.',
      )
    }
  }

  const handlePay = async (plan) => {
    if (!isPlanAvailable(plan)) return

    setPayingPlanId(plan.id)
    try {
      const response = await ApiEndpoints.createSubscriptionOrder(plan.id)
      const { checkout } = response.data?.data || {}

      if (!checkout?.key || !checkout?.order_id) {
        toast.error('Invalid payment response from server.')
        return
      }

      const opened = await openRazorpayCheckout(checkout, handlePaymentSuccess)

      if (!opened) return
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Failed to create payment order. Please try again.',
      )
    } finally {
      setPayingPlanId(null)
    }
  }

  const displayPlans = useMemo(
    () => plans.filter(isPlanAvailable),
    [plans],
  )
  const groupedPlans = useMemo(
    () => groupPlansByBillingCycle(displayPlans),
    [displayPlans],
  )

  useEffect(() => {
    if (!groupedPlans.length) return
    if (!groupedPlans.some((group) => group.key === activeBillingTab)) {
      setActiveBillingTab(groupedPlans[0].key)
    }
  }, [groupedPlans, activeBillingTab])

  if (!canAccessSubscription) {
    return <Navigate to="/Dashboard" replace />
  }

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        paddingBottom: 24,
        marginTop: isDesktop ? -12 : 0,
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          background: '#F3F4F6',
          paddingLeft: 28,
          paddingRight: 28,
          paddingTop: 12,
          paddingBottom: 12,
          borderBottom: '1px solid #D1D3D4',
          boxSizing: 'border-box',
          ...(isDesktop
            ? {
                position: 'fixed',
                top: 0,
                left: sidebarWidth,
                right: 0,
                zIndex: 100,
                transition: 'left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              }
            : {
                position: 'relative',
                width: '100%',
                marginTop: 55,
              }),
        }}
      >
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {!logoError ? (
                  <img
                    src={logo}
                    alt="School Logo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>

              <div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Subscription Plans
                </h3>
                <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                  Choose a plan and subscribe your institute
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin/subscription')}
                title="Back to subscription"
                style={{
                  height: '40px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '0 12px',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >
                <BsArrowLeft size={16} />
                {!isMobile && 'Current Plan'}
              </button>
              <button
                type="button"
                onClick={fetchPlans}
                disabled={loading}
                title="Refresh"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <BsArrowClockwise size={18} />
              </button>
              {!isMobile && <ProfileDropdown />}
            </div>
      </div>

      {isDesktop && <div style={{ height: 84 }} aria-hidden="true" />}

      <div
        style={{
          marginTop: 24,
          marginLeft: isDesktop ? sidebarWidth : 0,
          width: isDesktop ? `calc(100% - ${sidebarWidth}px)` : '100%',
          maxWidth: isDesktop ? `calc(100% - ${sidebarWidth}px)` : '100%',
          boxSizing: 'border-box',
          paddingLeft: isDesktop ? (isCollapsed ? 32 : 40) : 16,
          paddingRight: isDesktop ? 32 : 16,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {loading ? (
          <Card
            className="border-0 shadow-sm"
            style={{ borderRadius: '12px', padding: '48px', textAlign: 'center' }}
          >
            <Spinner animation="border" variant="secondary" />
            <p
              className="mt-3 mb-0"
              style={{ color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Loading plans...
            </p>
          </Card>
        ) : error && plans.length === 0 ? (
          <Card
            className="border-0 shadow-sm"
            style={{ borderRadius: '12px', padding: '32px', textAlign: 'center' }}
          >
            <p style={{ color: '#991B1B', marginBottom: '16px' }}>{error}</p>
            <button
              type="button"
              onClick={fetchPlans}
              style={{
                background: '#223843',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 18px',
                fontWeight: 600,
              }}
            >
              Try Again
            </button>
          </Card>
        ) : displayPlans.length === 0 ? (
          <Card
            className="border-0 shadow-sm"
            style={{ borderRadius: '12px', padding: '32px', textAlign: 'center' }}
          >
            <p style={{ color: '#6b7280', margin: 0 }}>No subscription plans available right now.</p>
          </Card>
        ) : (
          <BillingCycleTabs
            groups={groupedPlans}
            activeTab={activeBillingTab}
            onTabChange={setActiveBillingTab}
            payingPlanId={payingPlanId}
            onPay={handlePay}
          />
        )}
      </div>
    </div>
  )
}

export default AdminSubscriptionPlans
